import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import {
  formatHm,
  hourLabels,
  normalizeSlotRange,
  slotCovered,
  slotsPerDay,
  type TimeSegment,
} from './slotMath';

export type PaintMenuOption = {
  id: string;
  label: string;
  color?: string;
};

export type RangeCommitMeta = {
  /** Ctrl/Cmd held — add to existing selection instead of replacing. */
  additive: boolean;
  /** Shift held — extend from selection anchor. */
  extend: boolean;
  clientX: number;
  clientY: number;
};

export type TimelinePaintGridProps = {
  slotMinutes?: number;
  segments: TimeSegment[];
  slotBackgrounds?: Array<string | null | undefined>;
  fillColor?: string;
  fillLabel?: string;
  readOnly?: boolean;
  className?: string;
  cellMinWidth?: number;
  cellHeight?: number;
  fillWidth?: boolean;
  selectedRanges?: TimeSegment[];
  /**
   * Anchor slot for Shift+extend (0-based slot index within this row).
   * When Shift is held, selection starts from this slot.
   */
  selectionAnchorSlot?: number | null;
  onRangeCommit?: (range: { startMinute: number; endMinute: number }, meta: RangeCommitMeta) => void;
  onPaint?: (range: { startMinute: number; endMinute: number }, optionId: string | null) => void;
  menuOptions?: PaintMenuOption[];
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
};

type DragState = {
  startSlot: number;
  endSlot: number;
  additive: boolean;
  extend: boolean;
} | null;

type PaintMenuState = {
  x: number;
  y: number;
  startMinute: number;
  endMinute: number;
};

const MENU_PAD = 8;

/** Keep a fixed popup fully inside the viewport; prefer anchoring near the pointer. */
function clampMenuPosition(
  anchorX: number,
  anchorY: number,
  width: number,
  height: number,
): { left: number; top: number; maxHeight: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxHeight = Math.max(120, vh - MENU_PAD * 2);

  let left = anchorX;
  let top = anchorY;

  if (left + width > vw - MENU_PAD) left = vw - MENU_PAD - width;
  if (left < MENU_PAD) left = MENU_PAD;

  // Prefer below the pointer; flip above when there isn't enough room.
  if (top + Math.min(height, maxHeight) > vh - MENU_PAD) {
    top = anchorY - Math.min(height, maxHeight);
  }
  if (top + Math.min(height, maxHeight) > vh - MENU_PAD) {
    top = vh - MENU_PAD - Math.min(height, maxHeight);
  }
  if (top < MENU_PAD) top = MENU_PAD;

  return { left, top, maxHeight };
}

function slotInRanges(slot: number, slotMinutes: number, ranges: TimeSegment[]): boolean {
  const startM = slot * slotMinutes;
  const endM = startM + slotMinutes;
  return ranges.some((r) => r.startMinute < endM && r.endMinute > startM);
}

function slotFromClientX(track: HTMLElement, clientX: number, count: number): number {
  const rect = track.getBoundingClientRect();
  if (rect.width <= 0) return 0;
  const ratio = (clientX - rect.left) / rect.width;
  return Math.max(0, Math.min(count - 1, Math.floor(ratio * count)));
}

const PaintContextMenu: React.FC<{
  menu: PaintMenuState;
  menuOptions: PaintMenuOption[];
  onPick: (optionId: string | null) => void;
  onDismiss: () => void;
}> = ({ menu, menuOptions, onPick, onDismiss }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() =>
    clampMenuPosition(menu.x, menu.y, 176, 48 + menuOptions.length * 40 + 40),
  );

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos(clampMenuPosition(menu.x, menu.y, rect.width, rect.height));
  }, [menu.x, menu.y, menuOptions.length]);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default"
        aria-label="Dismiss"
        onClick={onDismiss}
      />
      <div
        ref={menuRef}
        className="fixed z-50 min-w-[11rem] overflow-y-auto rounded-lg border border-black/10 bg-white py-1 shadow-xl dark:border-white/10 dark:bg-zinc-900"
        style={{
          left: pos.left,
          top: pos.top,
          maxHeight: pos.maxHeight,
        }}
      >
        <div className="sticky top-0 border-b border-black/5 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-500 dark:bg-zinc-900">
          {formatHm(menu.startMinute)} – {formatHm(menu.endMinute)}
        </div>
        {menuOptions.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
            onClick={() => onPick(opt.id)}
          >
            {opt.color ? (
              <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: opt.color }} />
            ) : (
              <span
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold text-white',
                  opt.id === 'W' && 'bg-blue-600',
                  opt.id === 'R' && 'bg-zinc-400',
                  opt.id === 'D' && 'bg-amber-500',
                )}
              >
                {opt.id}
              </span>
            )}
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          className="sticky bottom-0 flex w-full border-t border-black/5 bg-white px-3 py-2 text-left text-sm text-zinc-500 hover:bg-zinc-100 dark:bg-zinc-900 dark:hover:bg-zinc-800"
          onClick={() => onPick(null)}
        >
          Clear
        </button>
      </div>
    </>
  );
};

export const TimelinePaintGrid: React.FC<TimelinePaintGridProps> = ({
  slotMinutes = 30,
  segments,
  slotBackgrounds,
  fillColor,
  fillLabel,
  readOnly = false,
  className,
  cellMinWidth = 16,
  cellHeight = 28,
  fillWidth = false,
  selectedRanges = [],
  selectionAnchorSlot = null,
  onRangeCommit,
  onPaint,
  menuOptions = [],
  leading,
  trailing,
}) => {
  const count = slotsPerDay(slotMinutes);
  const slotsPerHour = 60 / slotMinutes;
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState>(null);
  const [menu, setMenu] = useState<PaintMenuState | null>(null);
  const dragRef = useRef<DragState>(null);

  const inDragRange = useCallback(
    (slot: number) => {
      if (!drag) return false;
      const lo = Math.min(drag.startSlot, drag.endSlot);
      const hi = Math.max(drag.startSlot, drag.endSlot);
      return slot >= lo && slot <= hi;
    },
    [drag],
  );

  const finishDrag = useCallback(
    (clientX: number, clientY: number) => {
      const current = dragRef.current;
      if (!current) return;
      const range = normalizeSlotRange(current.startSlot, current.endSlot, slotMinutes);
      const { additive, extend } = current;
      dragRef.current = null;
      setDrag(null);
      if (readOnly) return;

      if (onRangeCommit) {
        onRangeCommit(
          { startMinute: range.startMinute, endMinute: range.endMinute },
          { additive, extend, clientX, clientY },
        );
        return;
      }

      if (!onPaint) return;
      if (menuOptions.length === 0) {
        onPaint({ startMinute: range.startMinute, endMinute: range.endMinute }, 'paint');
        return;
      }
      setMenu({
        x: clientX,
        y: clientY,
        startMinute: range.startMinute,
        endMinute: range.endMinute,
      });
    },
    [menuOptions.length, onPaint, onRangeCommit, readOnly, slotMinutes],
  );

  const beginDrag = useCallback(
    (e: React.PointerEvent, slot: number) => {
      if (readOnly || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      trackRef.current?.setPointerCapture?.(e.pointerId);
      setMenu(null);

      const additive = e.ctrlKey || e.metaKey;
      const extend = e.shiftKey && !additive;
      let startSlot = slot;
      if (extend && selectionAnchorSlot != null && Number.isFinite(selectionAnchorSlot)) {
        startSlot = Math.max(0, Math.min(count - 1, selectionAnchorSlot));
      }

      const next: DragState = {
        startSlot,
        endSlot: slot,
        additive,
        extend,
      };
      dragRef.current = next;
      setDrag(next);
    },
    [count, readOnly, selectionAnchorSlot],
  );

  const moveDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current || !trackRef.current) return;
      const slot = slotFromClientX(trackRef.current, e.clientX, count);
      const prev = dragRef.current;
      if (prev.endSlot === slot) return;
      const next = { ...prev, endSlot: slot };
      dragRef.current = next;
      setDrag(next);
    },
    [count],
  );

  const totalMinWidth = count * cellMinWidth;

  return (
    <div className={cn('relative flex w-full items-stretch gap-2', className)}>
      {leading}
      <div
        className={cn(
          'relative flex w-full min-w-0 flex-1 select-none',
          !fillWidth && 'overflow-x-auto',
        )}
      >
        <div
          ref={trackRef}
          className="flex w-full touch-none"
          style={fillWidth ? undefined : { width: totalMinWidth, minWidth: totalMinWidth }}
          onPointerMove={moveDrag}
          onPointerUp={(e) => {
            if (!dragRef.current) return;
            finishDrag(e.clientX, e.clientY);
          }}
          onPointerCancel={() => {
            dragRef.current = null;
            setDrag(null);
          }}
        >
          {Array.from({ length: count }, (_, slot) => {
            const startM = slot * slotMinutes;
            const endM = startM + slotMinutes;
            const covered = slotCovered(startM, endM, segments);
            const bg = slotBackgrounds?.[slot];
            const draggingOver = inDragRange(slot);
            const multiSelected = slotInRanges(slot, slotMinutes, selectedRanges);
            const hourIndex = Math.floor(slot / slotsPerHour);
            const isHourStart = slot % slotsPerHour === 0;
            const zebra = hourIndex % 2 === 1;

            let background: string | undefined;
            // Operation overlays (slot backgrounds) win over work fill so WRH "W"
            // does not paint solid blue over vessel-operation colors.
            if (bg) background = bg;
            else if (covered && fillColor) background = fillColor;
            else if (zebra) background = 'rgba(0,0,0,0.035)';

            const solidWorkFill = Boolean(covered && fillColor && !bg);

            return (
              <div
                key={slot}
                title={formatHm(startM)}
                className={cn(
                  'border-r border-black/[0.06] text-center text-[11px] font-extrabold leading-none',
                  fillWidth ? 'min-w-0 flex-1' : 'shrink-0',
                  isHourStart && 'border-l border-l-black/20',
                  !readOnly && 'cursor-crosshair',
                  (draggingOver || multiSelected) && 'ring-2 ring-inset ring-sky-500',
                  multiSelected && !covered && 'bg-sky-500/25',
                  solidWorkFill && 'text-white',
                  covered && fillLabel && !solidWorkFill && 'text-blue-700 dark:text-sky-300',
                )}
                style={{
                  ...(fillWidth
                    ? { minWidth: cellMinWidth }
                    : { width: cellMinWidth, minWidth: cellMinWidth }),
                  height: cellHeight,
                  lineHeight: `${cellHeight}px`,
                  backgroundColor: multiSelected && !covered ? undefined : background,
                }}
                onPointerDown={(e) => beginDrag(e, slot)}
              >
                {covered && fillLabel ? fillLabel : null}
              </div>
            );
          })}
        </div>
      </div>
      {trailing}

      {menu && (
        <PaintContextMenu
          menu={menu}
          menuOptions={menuOptions}
          onDismiss={() => setMenu(null)}
          onPick={(optionId) => {
            onPaint?.({ startMinute: menu.startMinute, endMinute: menu.endMinute }, optionId);
            setMenu(null);
          }}
        />
      )}
    </div>
  );
};

export const TimelineHourHeader: React.FC<{
  slotMinutes?: number;
  className?: string;
  cellMinWidth?: number;
  fillWidth?: boolean;
}> = ({ slotMinutes = 30, className, cellMinWidth = 16, fillWidth = false }) => {
  const slotsPerHour = 60 / slotMinutes;
  const labels = hourLabels(slotMinutes);
  const hourWidth = slotsPerHour * cellMinWidth;
  return (
    <div className={cn('flex w-full text-[11px] font-medium tabular-nums text-zinc-500', className)}>
      <div
        className="flex w-full"
        style={fillWidth ? undefined : { width: 24 * hourWidth, minWidth: 24 * hourWidth }}
      >
        {labels.map(({ hour }) => (
          <div
            key={hour}
            className={cn(
              'border-l border-black/15 pl-0.5',
              fillWidth ? 'min-w-0 flex-1' : 'shrink-0',
            )}
            style={
              fillWidth
                ? { minWidth: hourWidth }
                : { width: hourWidth, minWidth: hourWidth }
            }
            title={`${String(hour).padStart(2, '0')}:00`}
          >
            {String(hour).padStart(2, '0')}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelinePaintGrid;
